import { useSearchParams } from "react-router";

import { ChangeHistorySection } from "@/api";
import {
  useActiveAssetInventorySubTab,
  useActiveBankruptcyChecklistSubTab,
  useActiveClientTab,
  useActiveDebtSettlementTab,
  useActiveMasterDataTab,
} from "@/pages/clients/client/hooks/useClientTabs";
import {
  AssetInventorySubTab,
  BankruptcyChecklistSubTab,
  CASE_NOTE_ID_PARAM,
  ClientMasterDataTab,
  ClientTab,
  DebtSettlementSubTab,
  NEW_SRV_PROCEEDING_ID,
  SRV_PROCEEDING_ID_PARAM,
} from "@/router/ClientRoutes";

const MASTER_DATA_SECTIONS: Partial<Record<ClientMasterDataTab, ChangeHistorySection>> = {
  [ClientMasterDataTab.PersonalData]: ChangeHistorySection.PersonalData,
  [ClientMasterDataTab.GoalsResults]: ChangeHistorySection.GoalsResults,
  [ClientMasterDataTab.MeasuresCauses]: ChangeHistorySection.MeasuresCauses,
  [ClientMasterDataTab.StatusCounselor]: ChangeHistorySection.StatusCounselor,
};

const ASSET_INVENTORY_SECTIONS: Partial<Record<AssetInventorySubTab, ChangeHistorySection>> = {
  [AssetInventorySubTab.PersonalData]: ChangeHistorySection.AssetPersonalData,
  [AssetInventorySubTab.Relatives]: ChangeHistorySection.AssetRelatives,
  [AssetInventorySubTab.Income]: ChangeHistorySection.AssetIncome,
  [AssetInventorySubTab.Expenses]: ChangeHistorySection.AssetExpenses,
  [AssetInventorySubTab.OwnClaims]: ChangeHistorySection.AssetOwnClaims,
  [AssetInventorySubTab.Assets]: ChangeHistorySection.AssetAssets,
  [AssetInventorySubTab.OtherPropertyRights]: ChangeHistorySection.AssetPropertyRights,
  [AssetInventorySubTab.Donations]: ChangeHistorySection.AssetDonations,
};

const BANKRUPTCY_CHECKLIST_SECTIONS: Partial<Record<BankruptcyChecklistSubTab, ChangeHistorySection>> = {
  [BankruptcyChecklistSubTab.DangerousDebts]: ChangeHistorySection.DangerousDebts,
  [BankruptcyChecklistSubTab.SpecialDebts]: ChangeHistorySection.SpecialDebts,
  [BankruptcyChecklistSubTab.BankruptcyConsequences]: ChangeHistorySection.BankruptcyConsequences,
};

/** What the change info and history of a view cover. */
export interface ChangeHistoryScope {
  section: ChangeHistorySection;
  /**
   * Logical id of the single collection entry the view shows, for a view that covers one entry at a
   * time — the opened case note (Aktenvermerk) of the notes page (Vermerke), the opened Verfahren of
   * the proceedings page, the claim (Forderung) of its dialog; undefined where the view owns the whole section's data.
   */
  entryId?: string;
}

function wholeSection(section: ChangeHistorySection | undefined): ChangeHistoryScope | undefined {
  return section ? { section } : undefined;
}

/**
 * Maps the active client case file (Kundenakt) route to the change-history scope it addresses: the section, plus
 * the opened entry on a page that edits one entry at a time. Returns undefined on pages without own
 * saveable data (stubs, computed views like the bankruptcy capability) and on an entry-scoped page
 * before an entry is selected — the change-info line stays hidden there. A page whose entry is
 * opened in a dialog rather than by the route is not mapped at all: the claims page
 * (Verbindlichkeiten) passes its own scope, see Client.tsx.
 */
export function useChangeHistoryScope(): ChangeHistoryScope | undefined {
  const [searchParams] = useSearchParams();
  const clientTab = useActiveClientTab();
  const masterDataTab = useActiveMasterDataTab();
  const debtSettlementTab = useActiveDebtSettlementTab();
  const assetInventorySubTab = useActiveAssetInventorySubTab();
  const bankruptcyChecklistSubTab = useActiveBankruptcyChecklistSubTab();

  switch (clientTab) {
    case ClientTab.Overview:
      return wholeSection(ChangeHistorySection.Overview);
    case ClientTab.ClientNote:
      return wholeSection(ChangeHistorySection.ClientNote);
    case ClientTab.Notes: {
      // The case notes page (Vermerke) shows one case note (Aktenvermerk) at a time and a client can
      // have hundreds of them, so its info line and history belong to the opened note rather than to
      // every note of the client. CaseNotesTab keeps the param pointing at the effective selection.
      const caseNoteId = searchParams.get(CASE_NOTE_ID_PARAM);
      return caseNoteId ? { section: ChangeHistorySection.CaseNotes, entryId: caseNoteId } : undefined;
    }
    case ClientTab.MasterData:
      return wholeSection(masterDataTab ? MASTER_DATA_SECTIONS[masterDataTab] : undefined);
    case ClientTab.DebtSettlement:
      switch (debtSettlementTab) {
        // No case for the claims page (Verbindlichkeiten): it edits one claim (Forderung) at a time
        // in a dialog, so the line and the history belong to the opened claim, not to the page.
        case DebtSettlementSubTab.AssetInventory:
          return wholeSection(assetInventorySubTab ? ASSET_INVENTORY_SECTIONS[assetInventorySubTab] : undefined);
        case DebtSettlementSubTab.BankruptcyChecklist:
          return wholeSection(
            bankruptcyChecklistSubTab ? BANKRUPTCY_CHECKLIST_SECTIONS[bankruptcyChecklistSubTab] : undefined
          );
        case DebtSettlementSubTab.SrvProceeding: {
          // A client can have several Verfahren and the page shows one at a time, so the line and the
          // history belong to the opened one. A Verfahren being drafted has no history yet.
          const proceedingId = searchParams.get(SRV_PROCEEDING_ID_PARAM);
          return proceedingId && proceedingId !== NEW_SRV_PROCEEDING_ID
            ? { section: ChangeHistorySection.SrvProceeding, entryId: proceedingId }
            : undefined;
        }
        default:
          return undefined;
      }
    default:
      return undefined;
  }
}
